// Header pills for `EditablePromptCard`. Extracted from the card to keep
// the parent file under the file-size hygiene soft cap (see
// `.cursor/rules/file-size-hygiene.mdc`).

import type { PromptRowDTO } from "@/lib/aiPrompting/client";

export function ReviewRequiredPill() {
  return (
    <span
      className="rounded-full border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-900 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-100"
      title="Edits to this prompt must go through the suggestion + review flow. Only an owner or platform admin can bypass."
    >
      Review required
    </span>
  );
}

export function SourcePill({ source }: { source: "override" | "catalog" }) {
  if (source === "override") {
    return (
      <span className="rounded-full border border-emerald-400 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100">
        Override active
      </span>
    );
  }
  return (
    <span className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
      Catalog default
    </span>
  );
}

type Health = PromptRowDTO["health"];

/**
 * Slice F: 24h health roll-up rendered as a small pill in the card
 * header. Three states:
 *   - No data           — total === 0 (neutral gray)
 *   - All green         — total > 0 && total_failures === 0 (emerald)
 *   - N failures / 24h  — total_failures > 0 (amber <10%, rose >=10%)
 *
 * Hover tooltip exposes the full outcome breakdown so a trainer can see
 * whether the failures are all_null vs upstream_error vs parse_failed
 * without leaving the card.
 */
export function TelemetryHealthPill({ health }: { health: Health }) {
  const window = `${health.window_hours}h`;

  if (health.total === 0) {
    return (
      <span
        className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
        title={`No extract runs in the last ${window}. The health pill turns green after at least one successful lead extract.`}
      >
        No recent activity
      </span>
    );
  }

  const tooltip = buildHealthTooltip(health);

  if (health.total_failures === 0) {
    return (
      <span
        className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
        title={tooltip}
      >
        Healthy · {health.total}/{window}
      </span>
    );
  }

  const hot = health.failure_rate >= 0.1;
  const cls = hot
    ? "rounded-full border border-rose-400 bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-900 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-100"
    : "rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100";
  return (
    <span className={cls} title={tooltip}>
      {health.total_failures} fail · {window}
    </span>
  );
}

/**
 * Renders the per-outcome breakdown shown in the pill's title attribute.
 * Exported for testing — pure string formatter, no React.
 */
export function buildHealthTooltip(health: Health): string {
  const lines: string[] = [
    `Last ${health.window_hours}h — ${health.total} extract run${health.total === 1 ? "" : "s"}`,
    `  success: ${health.by_outcome.success}`,
  ];
  if (health.by_outcome.all_null > 0) lines.push(`  all_null: ${health.by_outcome.all_null}`);
  if (health.by_outcome.parse_failed > 0) lines.push(`  parse_failed: ${health.by_outcome.parse_failed}`);
  if (health.by_outcome.upstream_error > 0) lines.push(`  upstream_error: ${health.by_outcome.upstream_error}`);
  if (health.by_outcome.server_error > 0) lines.push(`  server_error: ${health.by_outcome.server_error}`);
  if (health.total_failures > 0) {
    const pct = Math.round(health.failure_rate * 1000) / 10;
    lines.push(`Failure rate: ${pct}%`);
  }
  return lines.join("\n");
}
